/**
 * O bloco "Campanhas" do Portal do Parceiro comissionado (P-48; §5.8; DS-674 (d); DS-677).
 *
 * ## O que ele mostra, e o que nunca mostra
 *
 * As campanhas dos pedidos em que a empresa do portal é a parceira comissionada, quando a DigitalView liga a publicação
 * para o Parceiro: código, ref. externa, nome, período planejado, a situação (P-11) e os resultados publicados. O
 * VALOR da campanha não existe aqui: o contrato do cartão do Parceiro não tem o campo (P-48), e o cartão não tem onde
 * escrevê-lo. Com o conjunto de vínculos mudado, a campanha FICA, com "Em atualização" e sem números (DS-674 (d)).
 *
 * ## Ausência, e não estado vazio
 *
 * Lista vazia é "nada publicado para o Parceiro", que é o padrão (os interruptores nascem desligados): o bloco não
 * existe, como o Pipeline sem negócio. Um "nenhuma campanha" para todo parceiro de todo portal seria um aviso sobre
 * algo que ele nunca teve.
 *
 * ## Fora do retrato das comissões
 *
 * A consulta é própria e fica fora do `juntar` da página: os resultados de mídia não são parte do retrato financeiro,
 * e uma falha aqui não pode tirar do parceiro as comissões que ele veio conferir. A falha aparece NO bloco, com
 * "Tentar de novo". O 404 é a exceção: é a portal-api da versão anterior, que não tem a rota (N-1, a janela de
 * publicação e o recuo por imagem), e para ela o bloco simplesmente não existe, como antes desta versão.
 *
 * ## Um retrato novo da página é uma consulta nova do bloco
 *
 * A chave da consulta leva o retrato da página (o período e o status das comissões). A rota não tem filtro, mas a
 * página some e volta a cada troca de recorte, e o bloco que voltasse com a resposta da PRIMEIRA consulta redesenharia
 * como novos os números que um desligamento ou um vínculo novo já revogaram (§6 C3: revogar vale na próxima consulta).
 * Assim o bloco reconsulta sempre que reaparece: na troca do período ou do status, na recarga, na remontagem (a troca
 * de empresa) e no "Tentar de novo" (DS-677 (e)).
 */
import type { CampanhaDoParceiroNoPortal } from '@dv/contracts'
import { pedir } from '../api/cliente.js'
import { COR_DA_CAMPANHA, listaDeCampanhasDoParceiro } from '../campanha-no-portal.js'
import { Erro } from '../componentes/estados.js'
import { ResultadosDaCampanha, RotuloDosResultados } from '../componentes/resultados-da-campanha.js'
import { useEmpresas } from '../estado/empresas.js'
import { useConsulta } from '../estado/recurso.js'
import { dataBr } from '../formato.js'

/**
 * @param mostrar a página já desenhou o retrato dela. A consulta sai junto com as outras (em paralelo, na montagem), e o
 *                bloco só aparece depois do resto: nem antes das comissões, nem com um erro próprio por cima do erro da
 *                página inteira.
 * @param retrato a chave do retrato da página (o período e o status): mudou, o bloco consulta de novo.
 */
export function CampanhasDoParceiro({ mostrar, retrato }: { mostrar: boolean; retrato: string }) {
  const campanhas = useConsulta(`parceiro/campanhas|${retrato}`, () => pedir('parceiro/campanhas', listaDeCampanhasDoParceiro))
  const { recurso } = campanhas

  if (!mostrar || recurso.estado === 'carregando') return null
  if (recurso.estado === 'erro' && recurso.tipo === 'ausente') return null
  if (recurso.estado === 'pronto' && recurso.dados.length === 0) return null

  return (
    <section className="painel" data-teste="campanhas-do-parceiro">
      {/*
        O apoio é o rótulo fixo dos resultados, uma vez para a seção (D-203). A frase que descrevia o bloco ("Campanhas dos
        pedidos em que você participa…") saiu: o ⓘ da página já a diz, e o título também.
      */}
      <div className="titulo com-apoio">
        <div>
          <h2>Campanhas</h2>
          {recurso.estado === 'pronto' && <RotuloDosResultados resultados={recurso.dados.map((campanha) => campanha.resultados)} />}
        </div>
      </div>
      {recurso.estado === 'erro' ? (
        <div className="erro-da-secao">
          <Erro tipo={recurso.tipo} tentar={campanhas.recarregar} />
        </div>
      ) : (
        /* Uma campanha por linha, no cartão LEVE das campanhas do Cliente e dos negócios (D-134 · DS-531). */
        <div className="lista-cartoes">
          {recurso.dados.map((campanha) => (
            <CartaoDaCampanhaDoParceiro key={campanha.campanhaId} campanha={campanha} />
          ))}
        </div>
      )}
    </section>
  )
}

function CartaoDaCampanhaDoParceiro({ campanha }: { campanha: CampanhaDoParceiroNoPortal }) {
  const empresas = useEmpresas()
  return (
    <article className="cartao-item" data-teste="cartao-campanha-do-parceiro">
      <div className="cartao-topo">
        <p className="eyebrow">
          {campanha.codigo}
          {campanha.refExterna === null ? '' : ` · Ref. ${campanha.refExterna}`}
        </p>
        <span className={`badge ${COR_DA_CAMPANHA[campanha.situacao] ?? 'slate'}`} data-teste="situacao-da-campanha-do-parceiro">
          {campanha.situacao}
        </span>
      </div>
      <h3 title={campanha.nome}>{campanha.nome}</h3>
      {empresas.mostrar && (
        <p className="empresas-do-cartao" data-teste="empresas-da-campanha-do-parceiro">
          {empresas.nomesDe(campanha.empresaIds)}
        </p>
      )}
      <ResultadosDaCampanha resultados={campanha.resultados} />
      {/* O rodapé do cartão do Cliente, sem o investimento: o período planejado e nada mais (P-48). */}
      <p className="cartao-rodape" data-teste="rodape-da-campanha-do-parceiro">
        {campanha.inicio === campanha.fim ? dataBr(campanha.inicio) : <>{dataBr(campanha.inicio)} — {dataBr(campanha.fim)}</>}
      </p>
    </article>
  )
}
